/**
 * INYRON Universal Life Runtime
 *
 * Species-agnostic life simulation. Cognition is bounded decision-making, not
 * a claim that every creature runs a human brain. Species provide capability
 * profiles; individuals keep persistent personality, memory and causal history.
 */

import { LifeCausalityGraph, lifeCausalityGraph } from "./life-causality";

export type LifeTier = "dormant" | "ambient" | "active" | "heroic";
export type LifeAction = "idle" | "rest" | "eat" | "drink" | "explore" | "socialize" | "work" | "flee" | "hunt" | "protect" | "travel" | "investigate" | "play" | "follow" | "seek_shelter";
export interface LifeVec3 { x: number; y: number; z?: number }
export interface LifeMemory { id: string; at: number; kind: "event" | "place" | "individual" | "success" | "failure"; salience: number; summary: string; position?: LifeVec3; subjectId?: string }
export interface LifePersonality { curiosity: number; courage: number; sociability: number; aggression: number; caution: number; territoriality: number; empathy: number; discipline: number; impulsivity: number; creativity: number; playfulness: number }
export interface LifeDrives { hunger: number; thirst: number; fatigue: number; safety: number; social: number; curiosity: number; territory: number; purpose: number }
export interface LifeProfile { speciesId: string; cognition: number; perceptionRadius: number; learningRate: number; social: boolean; nocturnal?: boolean; defaultTier?: LifeTier; biologyArchetype?: import("./physiology").BiologyArchetype; biologyAuthority?: "shared" | "external"; genomeVariant?: string }
export interface LifeEntity { id: string; profile: LifeProfile; position: LifeVec3; personality: LifePersonality; drives: LifeDrives; action: LifeAction; actionSince: number; ageSeconds: number; alive: boolean; seed: number; memories: LifeMemory[]; knownIndividuals: Record<string, number>; knownPlaces: Record<string, number>; history: LifeMemory[]; tier: LifeTier; lastDecisionAt: number; decisionCount: number }
export interface LifeObservation { entityId: string; kind: "threat" | "food" | "water" | "shelter" | "individual" | "place" | "event" | "success" | "failure"; position?: LifeVec3; targetId?: string; intensity?: number; summary?: string }
export interface LifeDecision { entityId: string; action: LifeAction; score: number; reason: string; at: number }
export interface LifeWorldActionOutcome {
  id: string;
  entityId: string;
  action: LifeAction;
  success: boolean;
  relief?: Partial<LifeDrives>;
  targetId?: string;
  position?: LifeVec3;
  summary?: string;
  salience?: number;
}
export interface LifeSchedulerSnapshot { clock: number; cursor: number; tickBudget: number; lastProcessed: [string, number][] }
export interface UniversalLifeSnapshot { version: 1; now: number; accumulator: number; entities: LifeEntity[]; scheduler?: LifeSchedulerSnapshot }
export interface LifeSchedulerStats { population: number; tickBudget: number; cursor: number; processedLastTick: number; inspectedLastTick: number; interruptsLastTick: number; pendingInterrupts: number; clock: number }

const ACTIONS: LifeAction[] = ["idle", "rest", "eat", "drink", "explore", "socialize", "work", "flee", "hunt", "protect", "travel", "investigate", "play", "follow", "seek_shelter"];
const TIERS = new Set<LifeTier>(["dormant", "ambient", "active", "heroic"]);
const ACTION_SET = new Set<LifeAction>(ACTIONS);
const clamp = (v: number) => Math.max(0, Math.min(1, v));
const finite = (v: unknown, fallback = 0) => typeof v === "number" && Number.isFinite(v) ? v : fallback;
const positiveInt = (v: unknown, fallback: number) => Math.max(1, Math.trunc(finite(v, fallback)));
function mulberry32(seed: number) { let t = seed >>> 0; return () => { t += 0x6d2b79f5; let r = Math.imul(t ^ (t >>> 15), 1 | t); r ^= r + Math.imul(r ^ (r >>> 7), 61 | r); return ((r ^ (r >>> 14)) >>> 0) / 4294967296; }; }
function defaultPersonality(seed: number): LifePersonality { const r = mulberry32(seed); return { curiosity: r(), courage: r(), sociability: r(), aggression: r(), caution: r(), territoriality: r(), empathy: r(), discipline: r(), impulsivity: r(), creativity: r(), playfulness: r() }; }
function defaultDrives(): LifeDrives { return { hunger: .25, thirst: .2, fatigue: .1, safety: .15, social: .2, curiosity: .25, territory: .1, purpose: .15 }; }
function cloneVec(v: LifeVec3): LifeVec3 { return { x: v.x, y: v.y, ...(v.z === undefined ? {} : { z: v.z }) }; }
function cloneMemory(memory: LifeMemory): LifeMemory { return { ...memory, ...(memory.position ? { position: cloneVec(memory.position) } : {}) }; }
function cloneEntity(entity: LifeEntity): LifeEntity {
  return {
    ...entity,
    profile: { ...entity.profile },
    position: cloneVec(entity.position),
    personality: { ...entity.personality },
    drives: { ...entity.drives },
    memories: entity.memories.map(cloneMemory),
    history: entity.history.map(cloneMemory),
    knownIndividuals: { ...entity.knownIndividuals },
    knownPlaces: { ...entity.knownPlaces },
  };
}
function restorableEntity(raw: unknown): raw is LifeEntity {
  if (!raw || typeof raw !== "object") return false;
  const entity = raw as Partial<LifeEntity>;
  return typeof entity.id === "string" && entity.id.length > 0
    && !!entity.profile && typeof entity.profile.speciesId === "string" && entity.profile.speciesId.length > 0
    && !!entity.position && Number.isFinite(entity.position.x) && Number.isFinite(entity.position.y)
    && !!entity.personality && !!entity.drives
    && typeof entity.seed === "number" && Number.isFinite(entity.seed);
}
function sanitizeRestoredEntity(raw: LifeEntity): LifeEntity {
  const personality = defaultPersonality(raw.seed >>> 0);
  for (const key of Object.keys(personality) as (keyof LifePersonality)[]) personality[key] = clamp(finite(raw.personality?.[key], personality[key]));
  const drives = defaultDrives();
  for (const key of Object.keys(drives) as (keyof LifeDrives)[]) drives[key] = clamp(finite(raw.drives?.[key], drives[key]));
  const memories = Array.isArray(raw.memories) ? raw.memories.filter(Boolean).slice(-32).map(cloneMemory) : [];
  const history = Array.isArray(raw.history) ? raw.history.filter(Boolean).slice(-128).map(cloneMemory) : [];
  return {
    id: raw.id,
    profile: { ...raw.profile, cognition: clamp(finite(raw.profile.cognition)), perceptionRadius: Math.max(0, finite(raw.profile.perceptionRadius)), learningRate: clamp(finite(raw.profile.learningRate)), social: Boolean(raw.profile.social) },
    position: { x: finite(raw.position.x), y: finite(raw.position.y), ...(raw.position.z === undefined ? {} : { z: finite(raw.position.z) }) },
    personality,
    drives,
    action: ACTION_SET.has(raw.action) ? raw.action : "idle",
    actionSince: Math.max(0, finite(raw.actionSince)),
    ageSeconds: Math.max(0, finite(raw.ageSeconds)),
    alive: raw.alive !== false,
    seed: raw.seed >>> 0,
    memories,
    knownIndividuals: raw.knownIndividuals && typeof raw.knownIndividuals === "object" ? { ...raw.knownIndividuals } : {},
    knownPlaces: raw.knownPlaces && typeof raw.knownPlaces === "object" ? { ...raw.knownPlaces } : {},
    history,
    tier: TIERS.has(raw.tier) ? raw.tier : raw.profile.defaultTier ?? "ambient",
    lastDecisionAt: Math.max(0, finite(raw.lastDecisionAt)),
    decisionCount: Math.max(0, Math.trunc(finite(raw.decisionCount))),
  };
}

export class UniversalLifeRuntime {
  private readonly entities = new Map<string, LifeEntity>();
  private readonly causality: LifeCausalityGraph;
  private readonly observations = new Map<string, LifeObservation[]>();
  private readonly listeners = new Set<(event: LifeDecision | LifeMemory) => void>();
  private readonly lastProcessedClock = new Map<string, number>();
  private readonly interruptSet = new Set<string>();
  private schedule: string[] = [];
  private interruptQueue: string[] = [];
  private interruptHead = 0;
  private cursor = 0;
  private now = 0;
  private accumulator = 0;
  private schedulerClock = 0;
  private tickBudget: number;
  private processedLastTick = 0;
  private inspectedLastTick = 0;
  private interruptsLastTick = 0;

  constructor(options: { tickBudget?: number; causality?: LifeCausalityGraph } = {}) {
    this.tickBudget = positiveInt(options.tickBudget, 64);
    this.causality = options.causality ?? lifeCausalityGraph;
  }

  register(input: Partial<LifeEntity> & { id: string; profile: LifeProfile; position: LifeVec3 }): LifeEntity {
    const existing = this.entities.get(input.id);
    if (existing) { existing.position = { ...input.position }; existing.profile = { ...input.profile }; return existing; }
    const seed = input.seed ?? this.seedFor(input.id);
    const entity: LifeEntity = {
      id: input.id,
      profile: { ...input.profile },
      position: { ...input.position },
      personality: input.personality ? { ...input.personality } : defaultPersonality(seed),
      drives: input.drives ? { ...input.drives } : defaultDrives(),
      action: input.action ?? "idle",
      actionSince: input.actionSince ?? this.now,
      ageSeconds: input.ageSeconds ?? 0,
      alive: input.alive ?? true,
      seed,
      memories: input.memories?.map(cloneMemory) ?? [],
      knownIndividuals: { ...(input.knownIndividuals ?? {}) },
      knownPlaces: { ...(input.knownPlaces ?? {}) },
      history: input.history?.map(cloneMemory) ?? [],
      tier: input.tier ?? input.profile.defaultTier ?? "ambient",
      lastDecisionAt: input.lastDecisionAt ?? 0,
      decisionCount: input.decisionCount ?? 0,
    };
    this.entities.set(entity.id, entity);
    if (entity.alive) this.insertScheduled(entity.id);
    this.lastProcessedClock.set(entity.id, this.schedulerClock);
    const birth = this.causality.record({ at: this.now, kind: "birth", actorId: entity.id, summary: `registered:${entity.profile.speciesId}`, salience: 1 });
    this.emit({ id: `${entity.id}:birth:${this.now}`, at: this.now, kind: "event", salience: 1, summary: birth.summary });
    return entity;
  }
  unregister(id: string) { this.entities.delete(id); this.observations.delete(id); this.lastProcessedClock.delete(id); this.removeScheduled(id); this.interruptSet.delete(id); }
  get(id: string) { return this.entities.get(id); }
  all() { return [...this.entities.values()]; }
  snapshot(): UniversalLifeSnapshot {
    return {
      version: 1,
      now: this.now,
      accumulator: this.accumulator,
      entities: [...this.entities.values()].map(cloneEntity),
      scheduler: {
        clock: this.schedulerClock,
        cursor: this.cursor,
        tickBudget: this.tickBudget,
        lastProcessed: [...this.lastProcessedClock.entries()].filter(([id]) => this.entities.has(id)),
      },
    };
  }
  restore(snapshot: unknown): number {
    if (!snapshot || typeof snapshot !== "object") return 0;
    const candidate = snapshot as Partial<UniversalLifeSnapshot>;
    if (candidate.version !== 1 || !Array.isArray(candidate.entities)) return 0;
    const restored = candidate.entities.filter(restorableEntity).map(sanitizeRestoredEntity);
    this.entities.clear();
    this.observations.clear();
    this.lastProcessedClock.clear();
    this.interruptSet.clear();
    this.interruptQueue = [];
    this.interruptHead = 0;
    this.now = Math.max(0, finite(candidate.now));
    this.accumulator = Math.max(0, Math.min(.499999, finite(candidate.accumulator)));
    const scheduler = candidate.scheduler && typeof candidate.scheduler === "object" ? candidate.scheduler : undefined;
    this.schedulerClock = Math.max(0, finite(scheduler?.clock, this.now));
    this.tickBudget = positiveInt(scheduler?.tickBudget, this.tickBudget);
    for (const entity of restored) this.entities.set(entity.id, entity);
    this.schedule = restored.filter(entity => entity.alive).map(entity => entity.id).sort();
    this.cursor = this.schedule.length === 0 ? 0 : Math.max(0, Math.trunc(finite(scheduler?.cursor))) % this.schedule.length;
    if (Array.isArray(scheduler?.lastProcessed)) {
      for (const entry of scheduler.lastProcessed) {
        if (!Array.isArray(entry) || entry.length !== 2) continue;
        const [id, at] = entry;
        if (typeof id === "string" && this.entities.has(id)) this.lastProcessedClock.set(id, Math.max(0, finite(at, this.schedulerClock)));
      }
    }
    for (const entity of restored) if (!this.lastProcessedClock.has(entity.id)) this.lastProcessedClock.set(entity.id, this.schedulerClock);
    this.processedLastTick = 0;
    this.inspectedLastTick = 0;
    this.interruptsLastTick = 0;
    return restored.length;
  }
  clear() {
    this.entities.clear(); this.observations.clear(); this.lastProcessedClock.clear(); this.interruptSet.clear();
    this.schedule = []; this.interruptQueue = []; this.interruptHead = 0; this.cursor = 0;
    this.now = 0; this.accumulator = 0; this.schedulerClock = 0; this.processedLastTick = 0; this.inspectedLastTick = 0; this.interruptsLastTick = 0;
  }
  setTickBudget(maxEntities: number) { this.tickBudget = positiveInt(maxEntities, this.tickBudget); }
  schedulerStats(): LifeSchedulerStats {
    return {
      population: this.schedule.length,
      tickBudget: this.tickBudget,
      cursor: this.cursor,
      processedLastTick: this.processedLastTick,
      inspectedLastTick: this.inspectedLastTick,
      interruptsLastTick: this.interruptsLastTick,
      pendingInterrupts: this.interruptQueue.length - this.interruptHead,
      clock: this.schedulerClock,
    };
  }
  observe(observation: LifeObservation) {
    const entity = this.entities.get(observation.entityId); if (!entity || !entity.alive) return;
    const list = this.observations.get(entity.id) ?? []; list.push(observation); if (list.length > 24) list.splice(0, list.length - 24); this.observations.set(entity.id, list);
    this.requestReplan(entity.id);
    const cause = this.causality.record({ at: this.now, kind: "observation", actorId: entity.id, targetId: observation.targetId, summary: observation.summary ?? observation.kind, salience: clamp(observation.intensity ?? .5) });
    this.remember(entity, { id: `${entity.id}:${this.now}:${list.length}`, at: this.now, kind: observation.targetId ? "individual" : "event", salience: clamp(observation.intensity ?? .5), summary: observation.summary ?? observation.kind, position: observation.position, subjectId: observation.targetId });
    if (observation.targetId) {
      const relation = observation.kind === "threat" ? "fear" : observation.kind === "individual" ? "affection" : undefined;
      if (relation) this.causality.relate(entity.id, observation.targetId, relation, relation === "fear" ? clamp(observation.intensity ?? .5) : .25, this.now, cause.id);
    }
  }
  /**
   * Commits a result reported by the authoritative physical world.
   * The world owns movement/resources; Universal Life owns drives, causal memory
   * and replanning. Event ids make the boundary idempotent across save/restore.
   */
  completeWorldAction(outcome: LifeWorldActionOutcome): boolean {
    const entity = this.entities.get(outcome.entityId);
    if (!entity?.alive || !outcome.id?.trim()) return false;
    if (entity.history.some(memory => memory.id === outcome.id)) return false;

    const salience = clamp(finite(outcome.salience, outcome.success ? .72 : .55));
    if (outcome.success && outcome.relief) {
      for (const key of Object.keys(outcome.relief) as (keyof LifeDrives)[]) {
        const amount = clamp(finite(outcome.relief[key], 0));
        entity.drives[key] = clamp(entity.drives[key] - amount);
      }
    }

    const summary = outcome.summary?.trim() ||
      `world-action:${outcome.action}:${outcome.success ? "success" : "failure"}`;
    const cause = this.causality.record({
      at: this.now,
      kind: "interaction",
      actorId: entity.id,
      targetId: outcome.targetId,
      summary,
      salience,
    });
    this.remember(entity, {
      id: outcome.id,
      at: this.now,
      kind: outcome.targetId ? "individual" : "event",
      salience,
      summary,
      position: outcome.position,
      subjectId: outcome.targetId,
    });
    if (outcome.targetId && outcome.action === "hunt") {
      this.causality.relate(entity.id, outcome.targetId, "rivalry", outcome.success ? .65 : .35, this.now, cause.id);
    } else if (outcome.targetId && outcome.action === "socialize") {
      this.causality.relate(entity.id, outcome.targetId, "affection", outcome.success ? .4 : .1, this.now, cause.id);
    }
    this.requestReplan(entity.id);
    return true;
  }

  remember(entity: LifeEntity, memory: LifeMemory) {
    entity.memories.push(memory); entity.history.push(memory); if (entity.memories.length > 32) entity.memories.splice(0, entity.memories.length - 32); if (entity.history.length > 128) entity.history.splice(0, entity.history.length - 128); if (memory.subjectId) entity.knownIndividuals[memory.subjectId] = memory.salience; this.emit(memory);
  }
  onEvent(listener: (event: LifeDecision | LifeMemory) => void) { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  tick(dt: number) {
    if (!(dt > 0) || !Number.isFinite(dt)) return;
    this.now += Math.min(dt, .25);
    this.schedulerClock += dt;
    this.accumulator += dt;
    if (this.accumulator < .5) return;
    this.accumulator = 0;
    this.processedLastTick = 0;
    this.inspectedLastTick = 0;
    this.interruptsLastTick = 0;
    const processed = new Set<string>();

    let interruptAttempts = 0;
    while (interruptAttempts < this.tickBudget && this.processedLastTick < this.tickBudget && this.interruptHead < this.interruptQueue.length) {
      const id = this.interruptQueue[this.interruptHead++];
      interruptAttempts += 1;
      this.inspectedLastTick += 1;
      this.interruptSet.delete(id);
      if (processed.has(id)) continue;
      if (this.processEntity(id, true)) {
        processed.add(id);
        this.processedLastTick += 1;
        this.interruptsLastTick += 1;
      }
    }
    if (this.interruptHead >= this.interruptQueue.length) { this.interruptQueue = []; this.interruptHead = 0; }

    let regularAttempts = 0;
    const remainingInspections = Math.max(0, this.tickBudget - this.inspectedLastTick);
    while (regularAttempts < remainingInspections && this.processedLastTick < this.tickBudget && this.schedule.length > 0) {
      if (this.cursor >= this.schedule.length) this.cursor = 0;
      const id = this.schedule[this.cursor];
      this.cursor = (this.cursor + 1) % this.schedule.length;
      regularAttempts += 1;
      this.inspectedLastTick += 1;
      if (processed.has(id)) continue;
      if (this.processEntity(id, false)) {
        processed.add(id);
        this.processedLastTick += 1;
      }
    }
  }
  fastForward(elapsedSeconds: number, maxSeconds = 7 * 24 * 60 * 60): number {
    if (!(elapsedSeconds > 0) || !Number.isFinite(elapsedSeconds)) return 0;
    const elapsed = Math.min(elapsedSeconds, Math.max(0, maxSeconds));
    this.now += elapsed;
    this.schedulerClock += elapsed;
    this.accumulator = 0;
    for (const entity of this.entities.values()) {
      if (!entity.alive) continue;
      entity.ageSeconds += elapsed;
      this.updateDrives(entity, elapsed);
      this.lastProcessedClock.set(entity.id, this.schedulerClock);
    }
    return elapsed;
  }

  nowSeconds(): number { return this.now; }

  kill(id: string, summary = "life ended") {
    const entity = this.entities.get(id); if (!entity) return;
    entity.alive = false; this.removeScheduled(id); this.interruptSet.delete(id);
    const cause = this.causality.record({ at: this.now, kind: "death", actorId: id, summary, salience: 1 });
    this.remember(entity, { id: `${id}:death:${this.now}`, at: this.now, kind: "event", salience: 1, summary: cause.summary });
  }

  revive(id: string): LifeEntity | undefined {
    const entity = this.entities.get(id);
    if (!entity) return undefined;
    if (!entity.alive) {
      entity.alive = true;
      this.insertScheduled(id);
      this.lastProcessedClock.set(id, this.schedulerClock);
    }
    return entity;
  }

  private processEntity(id: string, forceDecision: boolean) {
    const entity = this.entities.get(id);
    if (!entity || !entity.alive) return false;
    const previous = this.lastProcessedClock.get(id) ?? this.schedulerClock;
    const elapsed = Math.max(0, this.schedulerClock - previous);
    this.lastProcessedClock.set(id, this.schedulerClock);
    entity.ageSeconds += elapsed;
    this.updateDrives(entity, elapsed);
    if (forceDecision || this.now - entity.lastDecisionAt >= this.decisionInterval(entity)) this.decide(entity);
    this.observations.delete(entity.id);
    return true;
  }
  private requestReplan(id: string) {
    if (this.interruptSet.has(id)) return;
    this.interruptSet.add(id);
    this.interruptQueue.push(id);
  }
  private insertScheduled(id: string) {
    if (this.schedule.includes(id)) return;
    let low = 0; let high = this.schedule.length;
    while (low < high) { const mid = (low + high) >> 1; if (this.schedule[mid] < id) low = mid + 1; else high = mid; }
    this.schedule.splice(low, 0, id);
    if (low <= this.cursor && this.schedule.length > 1) this.cursor += 1;
    if (this.cursor >= this.schedule.length) this.cursor = 0;
  }
  private removeScheduled(id: string) {
    const index = this.schedule.indexOf(id); if (index < 0) return;
    this.schedule.splice(index, 1);
    if (index < this.cursor) this.cursor -= 1;
    if (this.schedule.length === 0) this.cursor = 0;
    else if (this.cursor >= this.schedule.length) this.cursor = 0;
  }
  private updateDrives(e: LifeEntity, dt: number) { e.drives.hunger = clamp(e.drives.hunger + dt * .004); e.drives.thirst = clamp(e.drives.thirst + dt * .006); e.drives.fatigue = clamp(e.drives.fatigue + dt * .003); e.drives.social = clamp(e.drives.social + dt * (e.profile.social ? .002 : .0004)); e.drives.curiosity = clamp(e.drives.curiosity + dt * (.001 + e.personality.curiosity * .001)); e.drives.safety = clamp(e.drives.safety + dt * .0005); }
  private decisionInterval(e: LifeEntity) { if (e.tier === "heroic") return 1.25; if (e.tier === "active") return 2; if (e.tier === "ambient") return 5; return 15; }
  private decide(e: LifeEntity) {
    const rng = mulberry32(e.seed + e.decisionCount * 7919); const obs = this.observations.get(e.id) ?? []; const threat = obs.filter(o => o.kind === "threat").reduce((m, o) => Math.max(m, o.intensity ?? .5), 0); const nearbyIndividual = obs.some(o => o.kind === "individual"); const hasFood = obs.some(o => o.kind === "food"); const hasWater = obs.some(o => o.kind === "water"); const hasShelter = obs.some(o => o.kind === "shelter");
    const score = (action: LifeAction) => { let s = .05 + rng() * .08; if (action === "flee") s += threat * (.55 + e.personality.caution * .4); if (action === "protect") s += e.personality.territoriality * .35 + e.personality.courage * .15; if (action === "eat") s += e.drives.hunger * (hasFood ? 1 : .25); if (action === "drink") s += e.drives.thirst * (hasWater ? 1 : .25); if (action === "rest") s += e.drives.fatigue * .9; if (action === "seek_shelter") s += threat * (hasShelter ? .8 : .15); if (action === "socialize") s += e.drives.social * e.personality.sociability * (nearbyIndividual ? 1 : .25); if (action === "explore") s += e.drives.curiosity * e.personality.curiosity * (1 - threat); if (action === "investigate") s += e.personality.curiosity * .35 * (1 - threat * .5); if (action === "hunt") s += e.personality.aggression * e.drives.hunger * .55; if (action === "play") s += e.personality.playfulness * (1 - threat) * .25; if (action === "work") s += e.personality.discipline * e.drives.purpose * .4; if (action === "travel") s += e.drives.curiosity * .25 + e.personality.creativity * .15; return s; };
    let best = ACTIONS[0]; let bestScore = -Infinity; for (const action of ACTIONS) { const s = score(action); if (s > bestScore) { best = action; bestScore = s; } } if (threat < .35 && rng() < .035) best = ACTIONS[Math.floor(rng() * ACTIONS.length)];
    e.action = best; e.actionSince = this.now; e.lastDecisionAt = this.now; e.decisionCount += 1;
    const reason = this.explain(e, best, threat);
    const cause = this.causality.record({ at: this.now, kind: "decision", actorId: e.id, summary: `${best}:${reason}`, salience: clamp(bestScore) });
    this.emit({ entityId: e.id, action: best, score: bestScore, reason, at: this.now });
    if (best === "socialize" || best === "follow" || best === "protect" || best === "hunt") {
      const target = obs.find(o => o.targetId)?.targetId;
      if (target) this.causality.relate(e.id, target, best === "hunt" ? "rivalry" : best === "protect" ? "leadership" : "affection", best === "hunt" ? .5 : .35, this.now, cause.id);
    }
  }
  private explain(e: LifeEntity, action: LifeAction, threat: number) { if (action === "flee") return `threat=${threat.toFixed(2)} caution=${e.personality.caution.toFixed(2)}`; if (action === "eat") return `hunger=${e.drives.hunger.toFixed(2)}`; if (action === "drink") return `thirst=${e.drives.thirst.toFixed(2)}`; if (action === "rest") return `fatigue=${e.drives.fatigue.toFixed(2)}`; if (action === "explore") return `curiosity=${e.personality.curiosity.toFixed(2)}`; if (action === "socialize") return `social=${e.drives.social.toFixed(2)} sociability=${e.personality.sociability.toFixed(2)}`; return `personality+drives+observations seed=${e.seed}`; }
  private emit(event: LifeDecision | LifeMemory) { for (const listener of this.listeners) listener(event); }
  private seedFor(id: string) { let h = 2166136261; for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619); return h >>> 0; }
}

export const universalLifeRuntime = new UniversalLifeRuntime();